type PublicOnboardingEnv = Partial<
  Record<
    | 'NEXT_PUBLIC_BUGSNAG_RELEASE_STAGE'
    | 'NEXT_PUBLIC_ENV'
    | 'ENV'
    | 'NODE_ENV'
    | 'NEXT_PUBLIC_ONBOARDING_ENABLED',
    string | undefined
  >
>

// Keep direct property reads so Next can inline client-side values.
// Passing the whole process.env object falls back to the browser process polyfill.
const getDefaultPublicEnv = (): PublicOnboardingEnv => ({
  NEXT_PUBLIC_BUGSNAG_RELEASE_STAGE:
    process.env.NEXT_PUBLIC_BUGSNAG_RELEASE_STAGE,
  NEXT_PUBLIC_ENV: process.env.NEXT_PUBLIC_ENV,
  ENV: process.env.ENV,
  NODE_ENV: process.env.NODE_ENV,
  NEXT_PUBLIC_ONBOARDING_ENABLED: process.env.NEXT_PUBLIC_ONBOARDING_ENABLED,
})

const TRUE_VALUES = new Set(['1', 'true', 'yes', 'on'])
const FALSE_VALUES = new Set(['0', 'false', 'no', 'off'])
const DEFAULT_ENABLED_STAGES = new Set([
  'development',
  'test',
  'staging',
  'preview',
])

const readBooleanEnv = (
  rawValue: string | undefined,
  fallback: boolean,
): boolean => {
  if (!rawValue) return fallback

  const normalized = rawValue.trim().toLowerCase()
  if (TRUE_VALUES.has(normalized)) return true
  if (FALSE_VALUES.has(normalized)) return false

  return fallback
}

const readReleaseStage = (env: PublicOnboardingEnv): string => {
  const rawStage = [
    env.NEXT_PUBLIC_BUGSNAG_RELEASE_STAGE,
    env.NEXT_PUBLIC_ENV,
    env.ENV,
    env.NODE_ENV,
  ].find((value) => value && value.trim().length > 0)

  return rawStage?.trim().toLowerCase() ?? 'unknown'
}

export function resolveOnboardingRuntimeOptions(
  env: PublicOnboardingEnv = getDefaultPublicEnv(),
) {
  const releaseStage = readReleaseStage(env)
  const defaultEnabled = DEFAULT_ENABLED_STAGES.has(releaseStage)

  return {
    releaseStage,
    onboardingEnabled: readBooleanEnv(
      env.NEXT_PUBLIC_ONBOARDING_ENABLED,
      defaultEnabled,
    ),
  }
}

export const isOnboardingEnabled = (): boolean =>
  resolveOnboardingRuntimeOptions().onboardingEnabled
